import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import NewsCard from "@/components/feature/NewsCard";
import { hotNews, categories } from "@/mocks/hotNews";
import { getCategory } from "@/utils/content";

export default function News() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") ?? "all";
  const q = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(q);
  const [visible, setVisible] = useState(8);

  const results = useMemo(() => {
    let arr = category === "all" ? [...hotNews] : hotNews.filter((n) => n.categoryId === category);
    if (q.trim()) {
      const k = q.trim().toLowerCase();
      arr = arr.filter((n) => n.title.toLowerCase().includes(k) || n.summary.toLowerCase().includes(k) || n.tag.toLowerCase().includes(k));
    }
    return arr;
  }, [category, q]);

  const setCategory = (id: string) => {
    setParams((p) => {
      if (id === "all") p.delete("category");
      else p.set("category", id);
      return p;
    }, { replace: true });
    setVisible(8);
  };

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setParams((p) => {
      if (keyword.trim()) p.set("q", keyword.trim());
      else p.delete("q");
      return p;
    }, { replace: true });
    setVisible(8);
  };

  const show = results.slice(0, visible);
  const crumbs = [
    { name: "首页", to: "/" },
    { name: "最新资讯", to: "/news" },
  ];
  if (category !== "all") crumbs.push({ name: getCategory(category).name, to: `/news?category=${category}` });

  const listLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category === "all" ? "全部" : getCategory(category).name}最新资讯 - 在线吃瓜`,
    url: `https://www.zgchigua.cn/news?category=${category}${q ? `&q=${q}` : ""}`,
    about: "热门话题与新鲜事",
  };

  return (
    <Layout>
      <Seo
        title={q ? `搜索“${q}”的新鲜事结果` : `${category === "all" ? "最新资讯" : `${getCategory(category).name}资讯`} - 热门话题新鲜事_在线吃瓜`}
        description={q ? `关于“${q}”的在线吃瓜新鲜事搜索结果，聚合全网热门话题、最新进展与即时更新的劲爆动态。` : `在线吃瓜${category === "all" ? "最新资讯" : getCategory(category).name + "频道"}，聚合全网热门话题、新鲜事与最新进展，即时更新不错过任何一条劲爆消息。`}
        keywords="在线吃瓜,热门话题,新鲜事,最新进展,即时更新,娱乐资讯,社会热点"
        path="/news"
        jsonLd={[listLd]}
      />

      <section className="container-x py-8 md:py-10">
        <nav className="flex items-center gap-1 text-xs text-foreground-500" aria-label="面包屑">
          {crumbs.map((c, i) => (
            <span key={c.name} className="inline-flex items-center gap-1">
              {i > 0 && <i className="ri-arrow-right-s-line"></i>}
              {i === crumbs.length - 1 ? <span className="text-foreground-700">{c.name}</span> : <Link to={c.to} className="hover:text-primary-600">{c.name}</Link>}
            </span>
          ))}
        </nav>

        <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="font-heading text-2xl md:text-4xl">{q ? `“${q}” 的搜索结果` : category === "all" ? "最新新鲜事" : `${getCategory(category).name}频道`}</h1>
            <p className="mt-2 text-sm text-foreground-600">
              {q ? `共找到 ${results.length} 条与“${q}”相关的热瓜内容` : `共 ${results.length} 条即时更新的热门话题与新鲜事，点击标题查看完整进展。`}
            </p>
          </div>
          <form onSubmit={onSearch} className="flex max-w-sm items-center gap-1 rounded-full bg-background-100 border border-background-200 p-1.5 focus-within:border-primary-300 transition-colors">
            <i className="ri-search-line pl-2 text-foreground-500"></i>
            <input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="搜索新鲜事关键词" className="flex-1 bg-transparent text-sm outline-none placeholder:text-foreground-400" />
            <button type="submit" className="chip bg-primary-500 px-4 py-2 text-sm text-background-50 hover:bg-primary-600 cursor-pointer">搜索</button>
          </form>
        </div>

        <div className="no-scrollbar mt-6 flex gap-2 overflow-x-auto pb-1">
          <button onClick={() => setCategory("all")} className={`chip px-4 py-2 text-sm font-medium whitespace-nowrap cursor-pointer ${category === "all" ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-primary-100"}`}>全部</button>
          {categories.map((c) => (
            <button key={c.id} onClick={() => setCategory(c.id)} className={`chip px-4 py-2 text-sm font-medium whitespace-nowrap cursor-pointer ${category === c.id ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-primary-100"}`}>
              <i className={c.icon}></i>{c.name}
            </button>
          ))}
        </div>

        {show.length > 0 ? (
          <>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {show.map((item) => (
                <NewsCard key={item.slug} item={item} />
              ))}
            </div>
            {results.length > visible && (
              <div className="mt-8 text-center">
                <button onClick={() => setVisible((v) => v + 8)} className="chip bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer">
                  加载更多新鲜事 <i className="ri-loader-4-line"></i>
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="mt-10 flex flex-col items-center rounded-2xl bg-background-100 py-16 text-center">
            <i className="ri-search-eye-line text-5xl text-background-300"></i>
            <h2 className="mt-4 font-heading text-xl">暂时没有找到相关内容</h2>
            <p className="mt-2 max-w-sm text-sm text-foreground-600">换个关键词，或切换到其他频道试试，也许这颗热瓜正在别处发酵。</p>
            <Link to="/news" className="chip mt-5 bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600">浏览全部新鲜事</Link>
          </div>
        )}
      </section>
    </Layout>
  );
}